import { Card, CardContent } from '@/components/ui/card';
import { Calculator } from 'lucide-react';
import { formatCurrency } from '@/utils/formatters';
import type { RecalculationSummary, ExistingInvestor } from '../types';

interface CalculationSummaryProps {
  summary: RecalculationSummary;
  selectedCount: number;
  existingInvestors: ExistingInvestor[];
}

export function CalculationSummary({
  summary,
  selectedCount,
  existingInvestors,
}: CalculationSummaryProps) {
  if (selectedCount === 0 && existingInvestors.length === 0) {
    return null;
  }

  return (
    <Card className="!bg-blue-50 border-blue-200 dark:!bg-blue-950 dark:border-blue-800">
      <CardContent className="pt-6">
        <div className="flex items-center gap-2 mb-4">
          <Calculator className="h-5 w-5 text-blue-600 dark:text-blue-400" />
          <h3 className="font-semibold text-blue-900 dark:text-blue-100">
            Calculation Summary
          </h3>
        </div>

        <div className="grid grid-cols-4 gap-4">
          {/* Total Call Amount */}
          <div>
            <p className="text-sm text-blue-600 dark:text-blue-400 mb-1">
              Total Call Amount
            </p>
            <p className="text-lg font-medium text-blue-900 dark:text-blue-100">
              {formatCurrency(summary.combined.totalDrawdown)}
            </p>
          </div>

          {/* Total Commitments */}
          <div>
            <p className="text-sm text-blue-600 dark:text-blue-400 mb-1">
              Total Commitments
            </p>
            <p className="text-lg font-medium text-blue-900 dark:text-blue-100">
              {formatCurrency(summary.combined.totalCommitment)}
            </p>
          </div>

          {/* New Investors */}
          <div>
            <p className="text-sm text-blue-600 dark:text-blue-400 mb-1">
              New Investors
            </p>
            <p className="text-lg font-medium text-blue-900 dark:text-blue-100">
              {summary.newInvestors.count}
            </p>
          </div>

          {/* Total Investors */}
          <div>
            <p className="text-sm text-blue-600 dark:text-blue-400 mb-1">
              Total Investors
            </p>
            <p className="text-lg font-medium text-blue-900 dark:text-blue-100">
              {summary.combined.totalInvestors}
            </p>
          </div>
        </div>

        {/* Additional Summary Details */}
        {selectedCount > 0 && existingInvestors.length > 0 && (
          <div className="mt-4 pt-4 border-t border-blue-200 dark:border-blue-800">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-blue-600 dark:text-blue-400 mb-1">
                  New Investor Allocation
                </p>
                <p className="font-medium text-blue-900 dark:text-blue-100">
                  {formatCurrency(summary.newInvestors.estimatedDrawdown)}{' '}
                  estimated (
                  {(summary.averageDrawdownPercentage * 100).toFixed(2)}% of
                  commitment)
                </p>
              </div>
              <div>
                <p className="text-blue-600 dark:text-blue-400 mb-1">
                  Existing Investor Total
                </p>
                <p className="font-medium text-blue-900 dark:text-blue-100">
                  {formatCurrency(summary.existingInvestors.totalDrawdown)}
                </p>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
